import React, { memo, useMemo } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import * as COLOR from '../../../../constants/colors';
import { FAQSectionProps } from './types';
import { TABS_DATA, DEFAULT_MESSAGE } from './simpleConstants';
import { FAQ_DATA } from './constants';
import { useFAQState, useFAQData } from './hooks';
import { FAQTabs, QuestionList, AnswerDisplay } from './components';


export const FaqSection: React.FC<FAQSectionProps> = memo(({ 
  className, 
  'data-testid': testId 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const {
    selectedTab,
    selectionByTab,
    expandedItems,
    handleTabChange,
    handleQuestionClick,
    handleMobileToggle,
  } = useFAQState();

  const {
    questionsForTab,
    hasQuestions,
    selectedQuestionIndex,
    activeQuestion,
    answerContent,
  } = useFAQData(selectedTab, selectionByTab, FAQ_DATA);

  const containerStyles = useMemo(() => ({
    width: "100%",
    maxWidth: 1280,
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    justifyContent: "center",
    px: 8,
    py: 0,
    boxSizing: "border-box",
    [theme.breakpoints.down(1280)]: {
      px: 2,
    },
  }), [theme.breakpoints]);

  const cardStyles = useMemo(() => ({
    width: { xs: "100%", md: 400 },
    height: { xs: "auto", md: 480 },
    minHeight: { xs: 200, md: 480 },
    p: 2,
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  }), []);

  const answerSectionStyles = useMemo(() => ({
    flex: 1,
    justifyContent: "center",
    height: "100%",
    alignItems: "flex-start",
    minWidth: { xs: "100%", md: 400 },
    display: { xs: "none", md: "flex" },
  }), []);

  return (
    <Box
      className={className}
      data-testid={testId}
      sx={containerStyles}
      role="region"
      aria-label="Frequently Asked Questions"
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 4, md: 8 }}
        alignItems="stretch"
        sx={{ width: "100%", maxWidth: 1280 }}
      >
        {}
        <Card sx={cardStyles}>
          <Stack spacing={3}>
            <Stack spacing={2} alignItems="center">
              <Typography
                variant="h4"
                component="h2"
                fontWeight="500"
                textAlign="center"
                sx={{ fontSize: 24 }}
                id="faq-title"
              >
                FAQ
              </Typography>

              <FAQTabs
                tabs={TABS_DATA}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
              />
            </Stack>

            <QuestionList
              questions={questionsForTab}
              selectedIndex={selectedQuestionIndex}
              onQuestionClick={handleQuestionClick}
              isMobile={isMobile}
              expandedItems={expandedItems}
              onMobileToggle={handleMobileToggle}
              selectedTab={selectedTab}
            />
          </Stack>
        </Card>

        {}
        <Stack spacing={2} sx={answerSectionStyles}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: 32,
              fontWeight: "bold",
              color: COLOR.GREY1,
              textAlign: "left",
              lineHeight: 1.25,
              letterSpacing: "0%",
              width: "100%",
            }}
            aria-describedby="faq-title"
          >
            {activeQuestion?.question ?? DEFAULT_MESSAGE}
          </Typography>

          {hasQuestions && activeQuestion && (
            <AnswerDisplay
              answer={answerContent}
              question={activeQuestion.question}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
});

FaqSection.displayName = 'FaqSection';